import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { importExportService } from '../services/importExportService';

type ComtradeRow = {
  rgDesc?: string;
  TradeValue?: number;
  NetWeight?: number;
  qty?: number;
  qtDesc?: string;
  ptTitle?: string;
  cmdDesc?: string;
  Period?: string;
  partnerCode?: number;
};

type ComtradeMeta = {
  source?: string;
  year?: string;
  reporterCode?: string;
  partnerCode?: string;
  cmdCode?: string;
  exportsStatus?: number;
  importsStatus?: number;
};

const asNumber = (v: unknown) => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim()) {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const ImportExportLab: React.FC = () => {
  const years = importExportService.getAvailableYears();
  const [year, setYear] = React.useState<number>(years[0] ?? new Date().getFullYear() - 2);
  const [commodity, setCommodity] = React.useState<string>('0902');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [dataset, setDataset] = React.useState<ComtradeRow[]>([]);
  const [meta, setMeta] = React.useState<ComtradeMeta | null>(null);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await importExportService.getRawComtrade({
        year,
        reporter: 'KEN',
        partner: 'all',
        commodity,
      });
      setDataset((Array.isArray(res.dataset) ? res.dataset : []) as ComtradeRow[]);
      setMeta(res.meta as ComtradeMeta);
    } catch (e) {
      setDataset([]);
      setMeta(null);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [commodity, year]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportsRows = React.useMemo(
    () => dataset.filter((r) => r?.rgDesc === 'Export'),
    [dataset],
  );
  const importsRows = React.useMemo(
    () => dataset.filter((r) => r?.rgDesc === 'Import'),
    [dataset],
  );

  const sumValue = React.useCallback((rows: ComtradeRow[]) => rows.reduce((s, r) => s + asNumber(r.TradeValue), 0), []);

  const aggregateByPartner = React.useCallback((rows: ComtradeRow[]) => {
    const map = new Map<string, { partner: string; value: number; netWeight: number; qty: number; unit: string }>();
    for (const row of rows) {
      const partner = typeof row?.ptTitle === 'string' && row.ptTitle.trim() ? row.ptTitle : 'Unknown';
      const current = map.get(partner) || { partner, value: 0, netWeight: 0, qty: 0, unit: row?.qtDesc || 'n/a' };
      current.value += asNumber(row?.TradeValue);
      current.netWeight += asNumber(row?.NetWeight);
      current.qty += asNumber(row?.qty);
      if (!current.unit || current.unit === 'n/a') current.unit = row?.qtDesc || current.unit;
      map.set(partner, current);
    }
    const arr = Array.from(map.values());
    arr.sort((a, b) => b.value - a.value);
    return arr;
  }, []);

  const topExportPartners = React.useMemo(() => aggregateByPartner(exportsRows).slice(0, 15), [aggregateByPartner, exportsRows]);
  const topImportPartners = React.useMemo(() => aggregateByPartner(importsRows).slice(0, 15), [aggregateByPartner, importsRows]);

  const partnerPlaceholders = React.useMemo(() => {
    const bad = dataset
      .map((r) => (typeof r?.ptTitle === 'string' ? r.ptTitle : ''))
      .filter((t) => t.startsWith('Partner '));
    return Array.from(new Set(bad)).slice(0, 15);
  }, [dataset]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Import/Export Lab
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Raw UN Comtrade (preview) inspection for Kenya. Use this page to validate import-side data and partner naming before updating the main UI.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Year"
              value={years.includes(year) ? year : ''}
              onChange={(e) => setYear(Number(e.target.value))}
              SelectProps={{ native: true }}
            >
              <option value="" />
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Commodity (HS code or TOTAL)"
              value={commodity}
              onChange={(e) => setCommodity(e.target.value.trim())}
              placeholder="0902, 0901, TOTAL"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button variant="contained" fullWidth onClick={fetchData} disabled={loading}>
              {loading ? 'Fetching…' : 'Fetch'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.light' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Exports (rows / value USD)
              </Typography>
              <Typography variant="h6">
                {exportsRows.length} / {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(sumValue(exportsRows))}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Source: {meta?.source || 'n/a'} • exportsStatus: {meta?.exportsStatus ?? 'n/a'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Imports (rows / value USD)
              </Typography>
              <Typography variant="h6">
                {importsRows.length} / {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(sumValue(importsRows))}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                year: {meta?.year || String(year)} • importsStatus: {meta?.importsStatus ?? 'n/a'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {partnerPlaceholders.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Partner label issues
          </Typography>
          <Typography variant="body2" color="text.secondary">
            These indicate partner names were not resolved. After redeploying the function, this list should be empty.
          </Typography>
          <Box sx={{ mt: 1 }}>
            {partnerPlaceholders.map((p) => (
              <Typography key={p} variant="caption" display="block">
                {p}
              </Typography>
            ))}
          </Box>
        </Paper>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Top Import Sources (by USD)
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Source</TableCell>
                  <TableCell align="right">Value (USD)</TableCell>
                  <TableCell align="right">Net Wgt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topImportPartners.map((r) => (
                  <TableRow key={r.partner}>
                    <TableCell>{r.partner}</TableCell>
                    <TableCell align="right">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(r.value)}
                    </TableCell>
                    <TableCell align="right">{new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(r.netWeight)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Top Export Destinations (by USD)
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Destination</TableCell>
                  <TableCell align="right">Value (USD)</TableCell>
                  <TableCell align="right">Net Wgt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topExportPartners.map((r) => (
                  <TableRow key={r.partner}>
                    <TableCell>{r.partner}</TableCell>
                    <TableCell align="right">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(r.value)}
                    </TableCell>
                    <TableCell align="right">{new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(r.netWeight)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ImportExportLab;

